import { React, useEffect, useState } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { Link } from "react-router-dom";
import { getHomePageStats } from "../services/PWSServices";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

const home_image = "/images/PFAS_Sampling_City_of_Banning_April2024-edit_1.jpg";

const progressColorsAccent = {
  textColor: "#d97a27",
  pathColor: "#d97a27",
  trailColor: "#f2f1f2",
  pathTransitionDuration: 0.5,
};
const getPercentageValue = (num, total, type) => {
  let percent = (num / total) * 100;
  if (type == "pie") {
    if (percent < 1) {
      return percent.toFixed(2);
    }
    else if (percent >= 1 && percent < 10) {
      return percent.toFixed(1);
    }
    else {
      return Math.floor(percent);
    }
  }
  else {
    return Math.round(percent);
  }

}

ChartJS.register(ArcElement, Tooltip, Legend);

const displayWithComma = (num) =>
  num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const ShowCircularBar = ({ progress, title, resolved, total }) => {
  return (
    <div className="w-full text-center flex flex-col justify-center items-center gap-2">
      <div
        className="w-full flex justify-center"
        style={{ width: 150, height: 150 }}
      >
        <CircularProgressbar
          value={progress}
          text={`${progress.toString()}%`}
          styles={buildStyles(progressColorsAccent)}
        />
      </div>
      <div className="w-full text-center">
        <p className="text-base break-words text-gray-600">{title}</p>
        <p className="text-sm break-words text-gray-500">
          {resolved} of
          <span>&nbsp;{total}</span>
        </p>
      </div>
    </div>
  );
};
const Home = () => {
  const [programStatus, setProgramStatus] = useState([
    {
      resolved: 0,
      total: 0,
      title: "Water Systems Registered",
      progress: 0,
    },
    {
      resolved: 0,
      total: 0,
      title: "Wells Scheduled",
      progress: 0,
    },
    {
      resolved: 0,
      total: 0,
      title: "Wells Sampled",
      progress: 0,
    },
    {
      resolved: 0,
      total: 0,
      title: "Wells with Results",
      progress: 0,
    },
  ]);

  const [dataStatus, setDataStatus] = useState("loading");

  useEffect(() => {
    const fetchData = async () => {
      const response = await getHomePageStats();
      if (!('data' in response)) {
        setProgramStatus(prev => null);
        return;
      }

      const {
        registered_pws,
        total_pws,
        total_wells,
        scheduled_wells,
        wells_sampled,
        wells_tested,
      } = response?.data?.program_status_stats;
      const totalPWSStr = displayWithComma(total_pws);
      const totalWellStr = displayWithComma(total_wells);
      setProgramStatus([
        {
          resolved: displayWithComma(registered_pws),
          total: totalPWSStr,
          title: "Water Systems Registered",
          progress: getPercentageValue(+registered_pws, +total_pws, "circle"),
        },
        {
          resolved: displayWithComma(scheduled_wells),
          total: totalWellStr,
          title: "Wells Scheduled",
          progress: getPercentageValue(+scheduled_wells, +total_wells, "circle"),
        },
        {
          resolved: displayWithComma(wells_sampled),
          total: totalWellStr,
          title: "Wells Sampled",
          progress: getPercentageValue(+wells_sampled, +total_wells, "circle"),
        },
        {
          resolved: displayWithComma(wells_tested),
          total: totalWellStr,
          title: "Wells with Results",
          progress: getPercentageValue(+wells_tested, +total_wells, "circle"),
        },
      ]);
    };

    fetchData();
  }, []);

  return (
    <section className="my-8 text-primary w-full">
      <h1 className="text-2xl md:text-3xl font-bold md:ml-8">Welcome</h1>
      <div className="w-full bg-white my-6 rounded-xl h-fit overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          <div className="px-4 md:px-8 py-6 lg:w-[60%]">
            {/*<input className='bg-accent w-full rounded-lg text-white p-2 focus:outline-none mb-5'value='The sign in feature of this site is currently unavailable. Please try again later.'/>*/}
            <h2 className="font-bold text-lg">Program Overview</h2>
            <div className="text-sm md:text-base font-semibold my-4 text-gray-600">
              <h2>
                This program provides technical assistance to water systems serving disadvantaged communities and
                severely disadvantaged communities for collecting and analyzing drinking water well samples for Per-and Polyfluoroalkyl Substances (PFAS) as required by State Water Board Order&nbsp;
                <a href="https://www.waterboards.ca.gov/drinking_water/certlic/drinkingwater/pfas_ddw_general_order/docs/pfas-general-order-dw-2024-0002-ddw.pdf" target="_blank">
                  <u>DW 2024-0002-DDW</u>
                </a>. The wells that qualify for testing are in&nbsp;
                <a href="https://www.waterboards.ca.gov/drinking_water/certlic/drinkingwater/pfas_ddw_general_order/docs/exhibit-a-list-of-sources-subject-to-general-order-dw-2024-0002-ddw.pdf" target="_blank">
                  <u>Attachment A</u>
                </a> of the order.
                <br />
                <br />
                Sampling and analysis services are provided by the Office of Water Programs and our partners at no cost to the water systems. All wells that qualified for sampling under this program have been registered.
                <br />
                <br />
                To access this portal and track the testing status for each of your wells, you must create an account.
                <br />
              </h2>
              <br />
              <Link to="/signin">
                <button className="rounded-full px-10 py-2 bg-secondary text-white hover:bg-primary/80 duration-100">
                  Get Started!
                </button>
              </Link>
              <br />
              <br />
              <p className="text-sm text-black/50">
                Use the email address on file with the State Water Board for
                your water system’s Administrative Contact, Designated Operator
                in Charge, Water Quality Sampler, or Operator to create an
                account.
              </p>
            </div>
          </div>
          <div className="lg:w-[40%] flex-grow">
            <img
              src={home_image}
              alt="Sacramento State Logo"
              className="rounded-b-xl lg:rounded-r-xl lg:rounded-b-none aspect-video object-cover h-full w-full md:w-auto"
            />
          </div>
        </div>
      </div>
      {programStatus && dataStatus !== "error" && (
        <div className="my-6">
          <h1 className="text-2xl md:text-3xl font-bold md:ml-8">
            Program Status
          </h1>
          <div className="w-full my-4 bg-white px-4 md:px-8 rounded-xl">
            <div className="p-4 xl:p-8 flex flex-wrap xl:flex-nowrap font-semibold gap-8">
              {programStatus.map((obj) => (
                <ShowCircularBar
                  key={obj.title}
                  progress={obj.progress}
                  title={obj.title}
                  resolved={obj.resolved}
                  total={obj.total}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Home;
